import {gql} from "@apollo/client";
import {mutate, query} from "../../lib/apollo";
import {searchBrokersOptions, searchSchoolsOptions} from "../campaigns/campaignsService";
import {searchProgramSets} from "../programSets/programSetsService";

export {searchSchoolsOptions, searchBrokersOptions};

const EVENT_DETAIL_FIELDS = `
    id
    hpPostEventId
    postUrl
    schoolId
    schoolName
    brokerId
    brokerName
    programSetId
    programSetName
    active
    constraintMilitary
    constraintGender
    constraintHsLower
    constraintHsHigher
    constraintEduLower
    constraintEduHigher
    constraintDegreeList
    constraintClassesList
    constraintWorkingList
`;

export async function fetchHpEvent(id: number) {
    const q = gql`
        query q($id: Int!) {
            hpEventDetail(id: $id) {
                ${EVENT_DETAIL_FIELDS}
            }
        }
    `;
    const res = await query(q, {id});
    return res?.hpEventDetail || null;
}

export async function hpEventUpsert(input: any) {
    const m = gql`
        mutation m($input: HpEventUpsertInput!) {
            hpEventUpsert(input: $input) {
                ${EVENT_DETAIL_FIELDS}
            }
        }
    `;
    const res = await mutate(m, {input});
    return res?.hpEventUpsert || null;
}

export async function searchFieldTemplateOptions(search: string) {
    const q = gql`
        query ($input: PostEventsInput!) {
            postEvents(input: $input) {
                items {
                    id
                    hpPostEventId
                    schoolName
                    brokerName
                    programSetName
                }
            }
        }
    `;
    const res = await query(q, {
        input: {limit: 100, skip: 0, search},
    });
    return (res?.postEvents?.items || []).map((p: any) => ({
        id: p.hpPostEventId,
        value: `${p.schoolName}(${p.brokerName || "None"})[${p.id}][${p.hpPostEventId}][${p.programSetName}]`,
        eventId: p.id,
        hpPostEventId: p.hpPostEventId,
    }));
}

export async function getFieldTemplateById(hpPostEventId: number, includeInactive = true) {
    const q = gql`
        query ($input: PostEventsInput!) {
            postEvents(input: $input) {
                items {
                    id
                    hpPostEventId
                    schoolName
                    brokerName
                    programSetName
                }
            }
        }
    `;
    // Search by fields id via numeric search; filter client-side to exact template
    const res = await query(q, {
        input: {
            limit: 50,
            skip: 0,
            search: String(hpPostEventId),
            includeInactive: !!includeInactive,
        },
    });
    const match = (res?.postEvents?.items || []).find(
        (p: any) => Number(p.hpPostEventId) === Number(hpPostEventId)
    );
    if (!match) {
        return {
            id: hpPostEventId,
            value: `Field template [${hpPostEventId}]`,
            hpPostEventId,
        };
    }
    return {
        id: match.hpPostEventId,
        value: `${match.schoolName}(${match.brokerName || "None"})[${match.id}][${match.hpPostEventId}][${match.programSetName}]`,
        eventId: match.id,
        hpPostEventId: match.hpPostEventId,
    };
}

export async function searchProgramSetOptions(search: string) {
    const res = await searchProgramSets(100, search);
    return (res.programSets || []).map((p: any) => ({
        id: p.id,
        value: `${p.name} [${p.id}]`,
        programSetId: p.id,
        programSetName: p.name,
    }));
}

export async function getProgramSetOptionById(id: number) {
    const res = await searchProgramSets(20, String(id));
    const match = (res.programSets || []).find((p: any) => Number(p.id) === Number(id));
    if (match) {
        return {
            id: match.id,
            value: `${match.name} [${match.id}]`,
            programSetId: match.id,
            programSetName: match.name,
        };
    }
    return {
        id,
        value: `Program set [${id}]`,
        programSetId: id,
        programSetName: "",
    };
}

export const EDU_LEVELS = [
    {id: 1, label: "Some High School"},
    {id: 2, label: "GED"},
    {id: 3, label: "High School"},
    {id: 4, label: "Some College"},
    {id: 5, label: "Diploma/Certificate"},
    {id: 6, label: "Associate Degree"},
    {id: 7, label: "Bachelor Degree"},
    {id: 8, label: "Master Degree"},
    {id: 9, label: "PhD"},
];

export const DEGREE_OPTIONS = [
    {id: 5, label: "Diploma"},
    {id: 6, label: "Associate's"},
    {id: 7, label: "Bachelor's"},
    {id: 8, label: "Master's"},
    {id: 9, label: "PhD"},
];

export const CLASS_OPTIONS = [
    {id: 1, label: "Online"},
    {id: 0, label: "In a classroom"},
    {id: 2, label: "Either way"},
];

export const WORKING_OPTIONS = [
    {id: 1, label: "Yes, full-time"},
    {id: 2, label: "Yes, part-time"},
    {id: 0, label: "Not currently"},
];

export function parseCsvInts(s: string | null | undefined): number[] {
    if (!s || !String(s).trim()) {
        return [];
    }
    return String(s)
        .split(",")
        .map((x) => Number(x.trim()))
        .filter((n) => Number.isFinite(n));
}

export function hsYearOptions() {
    const max = new Date().getFullYear() + 1;
    const years: number[] = [];
    for (let y = max; y >= 1960; y--) {
        years.push(y);
    }
    return years;
}
